/**
 * Which bottom-tab owns the view the user is currently on.
 *
 * The tab bar pins five destinations, but the app has ~50 views and the bar is
 * rendered on all of them (BOTTOM_NAV_HIDDEN_VIEWS is empty). Selection must
 * therefore answer for every view, not just the pinned five: resolving an
 * unmapped view to the first tab lit Home in brand purple while the user was
 * on Calendar, Sales, Settings or Profile.
 *
 * More is the default owner because that is what More IS here: MoreView lists
 * every destination not pinned to this bar (it filters the sidebar groups by
 * PINNED_TAB_VIEW_IDS below), so a view that is not a pinned tab is reached
 * through More. That is also the standard tab-bar More convention.
 */
import { getSidebarNavGroups } from './sidebarNavConfig';

/** The views pinned to the bar, in the order they appear. */
export const PINNED_TAB_VIEW_IDS = [
  'dashboard',
  'projects',
  'messages',
  'notifications',
] as const;

export const MORE_TAB_ID = 'more';

/**
 * Views that belong to a pinned tab without being it. Drilling into a project
 * or opening the create flow has not moved the user to a new destination, so
 * the bar keeps Projects lit rather than handing selection to More.
 */
const TAB_OWNER_BY_VIEW: Record<string, string> = {
  dashboard: 'dashboard',
  projects: 'projects',
  'project-detail': 'projects',
  'add-project': 'projects',
  messages: 'messages',
  notifications: 'notifications',
  [MORE_TAB_ID]: MORE_TAB_ID,
};

/** The id of the tab that should read as selected for `currentView`. */
export function resolveActiveBottomNavTabId(currentView: string): string {
  return TAB_OWNER_BY_VIEW[currentView] ?? MORE_TAB_ID;
}

/**
 * The badge count the bar's own More tab should show: the sum of every
 * unread count sitting on a destination reached through More (Email, today —
 * Messages and Alerts are pinned tabs with their own badge and are excluded).
 * Built from the same `getSidebarNavGroups` call MoreView renders its list
 * from, filtered to the same not-pinned set, so a badge here can never drift
 * from what the More page itself will show once opened.
 */
export function computeMoreTabBadgeCount(context: {
  role: string | undefined;
  unreadEmailCount?: number;
}): number {
  const groups = getSidebarNavGroups({ role: context.role ?? 'member', unreadEmailCount: context.unreadEmailCount });
  const pinned = new Set<string>(PINNED_TAB_VIEW_IDS);

  return [...groups.mainNavItems, ...groups.crmNavItems, ...groups.adminNavItems]
    .filter((item) => !pinned.has(item.id))
    .reduce((total, item) => total + (item.badge ?? 0), 0);
}
